import React, { useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "./ui/button";
import { Card } from "./ui/card";
import { Camera, X, Upload, ImageIcon, Sparkles, Loader2 } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { FactCheckResult } from "./FactCheckResult";

const MAX_BYTES = 8 * 1024 * 1024; // 8 MB

function readFileAsDataURL(file) {
    return new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(r.result);
        r.onerror = () => reject(r.error);
        r.readAsDataURL(file);
    });
}

export function PhotoCheckPanel() {
    const [open, setOpen] = useState(false);
    const [file, setFile] = useState(null);
    const [preview, setPreview] = useState(null);
    const [note, setNote] = useState("");
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [dragOver, setDragOver] = useState(false);
    const inputRef = useRef(null);
    const navigate = useNavigate();

    const reset = () => {
        setFile(null);
        setPreview(null);
        setNote("");
        setResult(null);
    };

    const openPicker = () => inputRef.current?.click();

    const handleFile = async (f) => {
        if (!f) return;
        if (!f.type.startsWith("image/")) {
            toast.error("Please choose an image file");
            return;
        }
        if (f.size > MAX_BYTES) {
            toast.error("Image is too large (max 8 MB)");
            return;
        }
        setFile(f);
        setResult(null);
        try {
            const dataUrl = await readFileAsDataURL(f);
            setPreview(dataUrl);
        } catch {
            toast.error("Could not read the image");
        }
    };

    const submit = async () => {
        if (!preview) return;
        setLoading(true);
        setResult(null);
        try {
            const { data } = await api.post("/factcheck/image", {
                image_base64: preview,
                note: note.trim(),
            });
            setResult(data);
            setTimeout(() => {
                document.getElementById("photo-check-result")?.scrollIntoView({ behavior: "smooth", block: "start" });
            }, 60);
        } catch (e) {
            const detail = e?.response?.data?.detail;
            if (detail?.code === "daily_limit_reached") {
                toast.error("Daily free limit reached");
                navigate("/pricing?reason=limit");
            } else {
                toast.error(typeof detail === "string" ? detail : detail?.message || "Could not analyze image");
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <section
            className="max-w-6xl mx-auto px-6 lg:px-10 pb-8"
            data-testid="photo-check-section"
        >
            {/* CTA card — always visible right under hero */}
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
            >
                <Card
                    className="card-surface rounded-3xl p-6 md:p-8 relative overflow-hidden border-primary/30 badge-glow-primary"
                >
                    <div
                        aria-hidden
                        className="absolute -top-16 -right-16 w-64 h-64 rounded-full opacity-30 blur-3xl pointer-events-none"
                        style={{ background: "radial-gradient(circle, hsl(var(--primary)) 0%, transparent 70%)" }}
                    />
                    <div className="relative flex flex-col md:flex-row md:items-center gap-6 justify-between">
                        <div className="flex items-start md:items-center gap-4">
                            <div className="w-14 h-14 rounded-2xl bg-primary/20 border border-primary/40 flex items-center justify-center shrink-0">
                                <Camera className="h-6 w-6 text-primary" />
                            </div>
                            <div>
                                <div className="inline-block text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full bg-primary/20 text-accent border border-primary/40 mb-1.5">
                                    New
                                </div>
                                <h2 className="font-display text-2xl md:text-3xl text-accent tracking-tight leading-tight">
                                    Got a suspicious screenshot?
                                </h2>
                                <p className="text-muted-foreground text-sm md:text-base mt-1 max-w-xl leading-relaxed">
                                    Drop a photo of any text, email, DM, or ad — Real-Check reads it and tells you if it&apos;s a scam.
                                </p>
                            </div>
                        </div>
                        <Button
                            onClick={() => setOpen((v) => !v)}
                            size="lg"
                            data-testid="photo-check-open-button"
                            className="rounded-full h-12 px-7 bg-primary hover:bg-primary/90 text-primary-foreground font-semibold shadow-lg shadow-primary/30 shrink-0"
                        >
                            <Camera className="h-4 w-4 mr-2" />
                            {open ? "Close" : "Verify by photo"}
                        </Button>
                    </div>

                    <AnimatePresence>
                        {open && (
                            <motion.div
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: "auto" }}
                                exit={{ opacity: 0, height: 0 }}
                                transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                                className="overflow-hidden"
                            >
                                <div className="mt-6 pt-6 border-t border-white/10">
                                    <input
                                        ref={inputRef}
                                        type="file"
                                        accept="image/*"
                                        className="hidden"
                                        data-testid="photo-check-file-input"
                                        onChange={(e) => handleFile(e.target.files?.[0])}
                                    />

                                    {!preview ? (
                                        <div
                                            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                                            onDragLeave={() => setDragOver(false)}
                                            onDrop={(e) => {
                                                e.preventDefault();
                                                setDragOver(false);
                                                handleFile(e.dataTransfer.files?.[0]);
                                            }}
                                            onClick={openPicker}
                                            data-testid="photo-check-dropzone"
                                            className={`cursor-pointer rounded-2xl border-2 border-dashed transition-colors p-10 text-center ${
                                                dragOver ? "border-primary bg-primary/5" : "border-white/15 hover:border-primary/50 hover:bg-white/[0.02]"
                                            }`}
                                        >
                                            <div className="w-14 h-14 rounded-full bg-primary/15 border border-primary/40 flex items-center justify-center mx-auto mb-4">
                                                <Upload className="h-6 w-6 text-primary" />
                                            </div>
                                            <p className="text-accent font-semibold text-lg">Drop a screenshot here</p>
                                            <p className="text-muted-foreground text-sm mt-1">or click to choose an image · PNG, JPG, up to 8 MB</p>
                                        </div>
                                    ) : (
                                        <div className="grid md:grid-cols-2 gap-6 items-start">
                                            <div className="relative rounded-2xl overflow-hidden border border-white/10 bg-black/40">
                                                <img
                                                    src={preview}
                                                    alt="Uploaded preview"
                                                    className="w-full max-h-96 object-contain"
                                                    data-testid="photo-check-preview"
                                                />
                                                <button
                                                    onClick={reset}
                                                    className="absolute top-2 right-2 w-8 h-8 rounded-full bg-black/70 backdrop-blur border border-white/20 flex items-center justify-center hover:bg-black/90 transition-colors"
                                                    data-testid="photo-check-remove"
                                                    aria-label="Remove image"
                                                >
                                                    <X className="h-4 w-4 text-white" />
                                                </button>
                                            </div>
                                            <div>
                                                <label className="text-xs uppercase tracking-widest text-muted-foreground">
                                                    Add a note (optional)
                                                </label>
                                                <textarea
                                                    value={note}
                                                    onChange={(e) => setNote(e.target.value)}
                                                    placeholder="e.g. This came from a random text at 3am claiming to be my bank."
                                                    maxLength={500}
                                                    data-testid="photo-check-note-input"
                                                    className="mt-2 w-full min-h-24 rounded-xl bg-white/5 border border-white/10 text-accent placeholder:text-muted-foreground/70 focus:outline-none focus:border-primary/50 p-3 text-sm resize-none"
                                                />
                                                <div className="mt-4 flex flex-wrap gap-3">
                                                    <Button
                                                        onClick={submit}
                                                        disabled={loading}
                                                        data-testid="photo-check-submit-button"
                                                        className="rounded-full h-11 px-6 bg-primary hover:bg-primary/90 text-primary-foreground font-semibold shadow-lg shadow-primary/30"
                                                    >
                                                        {loading ? (
                                                            <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Analyzing…</>
                                                        ) : (
                                                            <><Sparkles className="h-4 w-4 mr-2" /> Analyze image</>
                                                        )}
                                                    </Button>
                                                    <Button
                                                        onClick={openPicker}
                                                        variant="secondary"
                                                        className="rounded-full h-11"
                                                        data-testid="photo-check-replace-button"
                                                    >
                                                        <ImageIcon className="h-4 w-4 mr-2" /> Choose another
                                                    </Button>
                                                </div>
                                                <p className="text-[11px] text-muted-foreground mt-3">
                                                    Your image is analyzed in-memory and never stored publicly.
                                                </p>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </Card>
            </motion.div>

            {result && (
                <div id="photo-check-result" className="mt-10">
                    <FactCheckResult result={result} />
                </div>
            )}
        </section>
    );
}
